<template>
  <Popover @apply-show="show" @hide="hide" :disabled="disabled">
    <div slot="popover" class="year-month-select">
      <div ref="list" class="year-month-list" v-close-popover>
        <ul v-for="({ year, group }, yearKey) in groupingYearMonths" :key="yearKey" :data-year="year">
          <li v-for="(date, key) in group" :key="key">
            <button
              :class="{
                'this-month': isThisMonth(date),
                selected: isSelected(date),
              }"
              @click="change(date)"
            >
              <em>{{ format(date, 'M') }}</em
              >月
            </button>
          </li>
        </ul>
      </div>
    </div>
    <slot />
  </Popover>
</template>

<script>
import { groupBy } from 'lodash'
import { format } from 'date-fns'
import Popover, { ClosePopover } from '@/components/molecules/Popover'

/**
 * 年月選択
 */
export default {
  name: 'YearMonthSelectPopover',
  components: {
    Popover,
  },
  directives: {
    ClosePopover,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    /** 年月 (v-model) */
    value: {
      type: String,
      required: true,
    },
    /** 年月の配列(Date型) */
    yearMonths: {
      type: Array,
      required: true,
    },
    /** 値の形式 */
    valueFormat: {
      type: String,
      required: false,
      default: 'YYYY-MM',
    },
    /** 非活性フラグ */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** リストアイテムの高さ */
    itemHeight: {
      type: Number,
      required: false,
      default: 64,
    },
    /** スクロール位置(リストアイテム個数) */
    scrollTo: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      isShow: false,
    }
  },
  computed: {
    groupingYearMonths() {
      const grouped = groupBy(this.yearMonths, date => format(date, 'YYYY'))
      return Object.entries(grouped)
        .map(([year, group]) => ({ year, group }))
        .reverse()
    },
    yearMonth: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('change', format(value, this.valueFormat))
      },
    },
  },
  methods: {
    format,
    change(value) {
      this.yearMonth = value
      this.hide()
    },
    isThisMonth(value) {
      return format(value, this.valueFormat) === format(new Date(), this.valueFormat)
    },
    isSelected(value) {
      return format(value, this.valueFormat) === this.value
    },
    show() {
      this.isShow = true
      this.$refs.list.scrollTo(0, this.itemHeight * this.scrollTo)
    },
    hide() {
      if (!this.isShow) return
      this.isShow = false
    },
  },
}
</script>

<style lang="postcss" scoped>
.year-month-select {
  position: relative;

  .year-month-list {
    border-radius: 5px;
    display: block;
    max-height: 50vh;
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
    overflow-y: scroll;
    -webkit-transform: translateZ(0); /* iOS Safariの慣性スクロール対策、メモリ消費に注意 */
    width: 254px;

    ul {
      border-radius: 5px;
      font-weight: bold;
      position: relative;

      &::before {
        color: var(--very-dark-gray-2);
        content: attr(data-year) '年';
        display: block;
        font-size: 15px;
        left: 15px;
        line-height: 1;
        position: absolute;
        top: 15px;
        z-index: 10;
      }
    }

    li {
      position: relative;
    }

    button {
      appearance: none;
      background-color: transparent;
      border: 0;
      border-radius: 0;
      color: var(--text-color);
      cursor: pointer;
      font-family: inherit;
      font-size: 21px;
      height: 64px;
      outline: none;
      padding: 0;
      width: 100%;

      em {
        font-size: 32px;
        font-style: normal;
        margin: 0 8px;
        text-align: right;
        width: 46px;
      }

      &::after {
        background-color: var(--border-color);
        bottom: 0;
        content: '';
        height: 1px;
        left: 0;
        margin: 0 2.5%;
        position: absolute;
        width: 95%;
      }

      &.selected {
        background-color: var(--very-light-yellow);
      }

      &.this-month {
        &::before {
          background-color: var(--very-dark-gray);
          border-radius: 8px;
          color: var(--white);
          content: '今月';
          display: block;
          font-size: 13px;
          height: 16px;
          line-height: 1.3;
          position: absolute;
          right: 23px;
          text-align: center;
          top: 23px;
          width: 40px;
        }
      }
    }
  }
}
</style>
